import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiX } from 'react-icons/fi';
import styles from './ExhibitionPhotoCarousel.module.css';

const SWIPE_DISTANCE = 40;

/**
 * Large photo carousel for an exhibition with every photo shown below it.
 * The cover photo leads; the rest follow in the organizer's order.
 */
const ExhibitionPhotoCarousel = ({ photos = [], title = 'Exhibition' }) => {
  const slides = [
    ...photos.filter((photo) => photo?.image_url && photo.is_cover),
    ...photos.filter((photo) => photo?.image_url && !photo.is_cover)
  ];
  const count = slides.length;
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchStart = useRef(null);
  const thumbRefs = useRef([]);

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  const go = useCallback((step) => {
    if (count < 2) return;
    setIndex((current) => (current + step + count) % count);
  }, [count]);

  useEffect(() => {
    thumbRefs.current[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [index]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setLightbox(false);
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightbox, go]);

  if (count === 0) return null;

  const current = slides[Math.min(index, count - 1)];

  const swipeHandlers = {
    onTouchStart: (event) => { touchStart.current = event.touches[0].clientX; },
    onTouchEnd: (event) => {
      if (touchStart.current === null) return;
      const distance = event.changedTouches[0].clientX - touchStart.current;
      touchStart.current = null;
      if (Math.abs(distance) > SWIPE_DISTANCE) go(distance < 0 ? 1 : -1);
    }
  };

  const arrows = (prevClass, nextClass) => count > 1 && (
    <>
      <button type="button" className={`${styles.arrow} ${prevClass}`} onClick={() => go(-1)} aria-label="Previous photo">
        <FiChevronLeft />
      </button>
      <button type="button" className={`${styles.arrow} ${nextClass}`} onClick={() => go(1)} aria-label="Next photo">
        <FiChevronRight />
      </button>
    </>
  );

  return (
    <div className={styles.carousel}>
      <div
        className={styles.stage}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${title} photos`}
        onKeyDown={(event) => {
          if (lightbox) return;
          if (event.key === 'ArrowLeft') go(-1);
          if (event.key === 'ArrowRight') go(1);
        }}
        {...swipeHandlers}
      >
        <img
          key={current.id || current.image_url}
          src={current.image_url}
          alt={`${title}, photo ${index + 1} of ${count}`}
          className={styles.slide}
          onClick={() => setLightbox(true)}
        />
        {arrows(styles.prev, styles.next)}
        <button type="button" className={styles.expand} onClick={() => setLightbox(true)} aria-label="View full screen">
          <FiMaximize2 />
        </button>
        {count > 1 && <span className={styles.counter}>{index + 1} / {count}</span>}
      </div>

      {count > 1 && (
        <div className={styles.thumbs}>
          {slides.map((photo, photoIndex) => (
            <button
              key={photo.id || photo.image_url}
              ref={(node) => { thumbRefs.current[photoIndex] = node; }}
              type="button"
              className={`${styles.thumb} ${photoIndex === index ? styles.thumbActive : ''}`}
              onClick={() => setIndex(photoIndex)}
              aria-label={`Show photo ${photoIndex + 1}`}
              aria-current={photoIndex === index ? 'true' : undefined}
            >
              <img src={photo.image_url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {lightbox && createPortal(
        <div className={styles.lightbox} role="dialog" aria-modal="true" aria-label={`${title} photos`} {...swipeHandlers}>
          <button type="button" className={styles.close} onClick={() => setLightbox(false)} aria-label="Close">
            <FiX />
          </button>
          <div className={styles.lightboxStage} onClick={(event) => { if (event.target === event.currentTarget) setLightbox(false); }}>
            <img
              key={current.id || current.image_url}
              src={current.image_url}
              alt={`${title}, photo ${index + 1} of ${count}`}
              className={styles.lightboxImage}
            />
            {arrows(styles.lightboxPrev, styles.lightboxNext)}
          </div>
          <div className={styles.lightboxCaption}>
            {title}{count > 1 ? ` · ${index + 1} / ${count}` : ''}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

ExhibitionPhotoCarousel.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    image_url: PropTypes.string,
    is_cover: PropTypes.bool
  })),
  title: PropTypes.string
};

export default ExhibitionPhotoCarousel;
